import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PodcastEpisode } from '@/types/episode.types';
import { Clip } from '@/types/clip.types';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { customToast } from '@/components/ui/custom-toast';
import { primeCelebrationSound } from '@/components/ui/celebration-toast';
import { primeNotificationSound } from '@/utils/sounds';
import { dateToUnixTimestamp } from '@/components/ui/date-time-picker';
import { normalizeStatus } from '@/utils/episode-helpers';

interface UseEpisodeActionsArgs {
  episodeId: string | undefined;
  episode: PodcastEpisode | null;
  clips: Clip[];
  setEpisode: React.Dispatch<React.SetStateAction<PodcastEpisode | null>>;
  setClips: React.Dispatch<React.SetStateAction<Clip[]>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setClipsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loadClips: () => void;
  setEditedTitle: React.Dispatch<React.SetStateAction<string>>;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
}

export function useEpisodeActions({
  episodeId,
  episode,
  clips,
  setEpisode,
  setClips,
  setError,
  setClipsLoading,
  loadClips,
  setEditedTitle,
  setDescription,
}: UseEpisodeActionsArgs) {
  const navigate = useNavigate();
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const refreshEpisode = useCallback(async () => {
    if (!episodeId) return;
    try {
      const ep = await PodcastEpisodeService.getEpisode(episodeId);
      if (ep) {
        const normalizedStatus = normalizeStatus(ep.status as string);
        setEpisode({ ...ep, status: normalizedStatus });
        setDescription(ep.description || '');
      }
    } catch (err) {
      console.error('Failed to refresh episode:', err);
    }
    loadClips();
  }, [episodeId, loadClips, setEpisode, setDescription]);

  // ── CRUD ───────────────────────────────────────────────────

  const handleArchive = useCallback(async () => {
    if (!episode) return;
    setActionInProgress('archive');
    try {
      await PodcastEpisodeService.archiveEpisode(episode.id);
      customToast.success('Episode archived');
      refreshEpisode();
    } catch (err: any) {
      setError(err.message || 'Failed to archive episode');
    } finally {
      setActionInProgress(null);
    }
  }, [episode, refreshEpisode, setError]);

  const handleUnarchive = useCallback(async () => {
    if (!episode) return;
    setActionInProgress('unarchive');
    try {
      await PodcastEpisodeService.unarchiveEpisode(episode.id);
      customToast.success('Episode unarchived - ready to reprocess');
      refreshEpisode();
    } catch (err: any) {
      customToast.error('Failed to unarchive', {
        description: err.message || 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode, refreshEpisode]);

  const handleDelete = useCallback(async () => {
    if (!episode) return;
    setActionInProgress('delete');
    try {
      await PodcastEpisodeService.deleteEpisode(episode.id);
      customToast.success('Episode deleted');
      navigate('../episodes');
    } catch (err: any) {
      setError(err.message || 'Failed to delete episode');
      setActionInProgress(null);
    }
  }, [episode, navigate, setError]);

  // ── Clip operations ────────────────────────────────────────

  const handleDeleteClip = useCallback(async (clipId: string) => {
    if (!episode) return;
    const clipToDelete = clips.find(c => c.id === clipId);
    const clipIndex = clips.findIndex(c => c.id === clipId);
    setClips(prev => prev.filter(c => c.id !== clipId));

    try {
      await PodcastEpisodeService.deleteClip(episode.id, clipId);
      refreshEpisode();
    } catch (err) {
      if (clipToDelete) {
        setClips(prev => {
          const newClips = [...prev];
          newClips.splice(clipIndex, 0, clipToDelete);
          return newClips;
        });
      }
      customToast.error('Failed to remove clip', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }, [episode, clips, refreshEpisode, setClips]);

  const handleMoveClip = useCallback(async (clipId: string, direction: 'up' | 'down') => {
    const currentIndex = clips.findIndex((c) => c.id === clipId);
    if (currentIndex === -1) return;
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= clips.length) return;

    const newClips = [...clips];
    [newClips[currentIndex], newClips[newIndex]] = [newClips[newIndex], newClips[currentIndex]];
    const updatedClips = newClips.map((clip, index) => ({ ...clip, position: index }));
    setClips(updatedClips);

    try {
      const newClipIds = updatedClips.map((c) => c.id);
      await PodcastEpisodeService.reorderClips(episode!.id, newClipIds);
      loadClips();
    } catch (err) {
      setClips(clips);
      customToast.error('Failed to reorder clips', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }, [episode, clips, loadClips, setClips]);

  const saveScriptForClip = useCallback(async (clipId: string, script: string) => {
    if (!episode) return;
    setActionInProgress(`save-script-${clipId}`);
    try {
      await PodcastEpisodeService.updateClip(episode.id, clipId, { script });
      setClips((prev) =>
        prev.map((clip) =>
          clip.id === clipId ? { ...clip, script } : clip
        )
      );
      customToast.success('Script saved');
    } catch (err) {
      customToast.error('Failed to save script', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode, setClips]);

  // ── Bulk operations ────────────────────────────────────────

  const handleGenerateScripts = useCallback(async () => {
    if (!episode) return;
    primeNotificationSound();
    setActionInProgress('generate-scripts');
    try {
      const result = await PodcastEpisodeService.generateScripts(episode.id);
      customToast.success(`Script generation started for ${result.clipCount} clips`);
      refreshEpisode();
    } catch (err) {
      customToast.error('Failed to start script generation', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode, refreshEpisode]);

  const handleApproveScripts = useCallback(async () => {
    if (!episode) return;
    setActionInProgress('approve-scripts');
    try {
      await PodcastEpisodeService.approveScripts(episode.id);
      refreshEpisode();
    } catch (err) {
      customToast.error('Failed to approve scripts', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode, refreshEpisode]);

  const handleRegenerateScript = useCallback(async (clipId: string) => {
    if (!episode) return;
    setActionInProgress(`regenerate-script-${clipId}`);
    try {
      await PodcastEpisodeService.regenerateClipScript(episode.id, clipId);
      customToast.success('Script regeneration started');
    } catch (err) {
      customToast.error('Failed to regenerate script', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode]);

  const handleGenerateAudio = useCallback(async () => {
    if (!episode) return;
    primeNotificationSound();
    setActionInProgress('generate-audio');
    try {
      const result = await PodcastEpisodeService.generateAudio(episode.id);
      customToast.success(`Audio generation started for ${result.clipCount} clips`);
      refreshEpisode();
    } catch (err) {
      customToast.error('Failed to start audio generation', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode, refreshEpisode]);

  const handleApproveAudio = useCallback(async () => {
    if (!episode) return;
    setActionInProgress('approve-audio');
    try {
      const result = await PodcastEpisodeService.approveAudio(episode.id);
      customToast.success(`${result.approvedCount} audio clips approved`);
      refreshEpisode();
    } catch (err) {
      customToast.error('Failed to approve audio', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode, refreshEpisode]);

  const handleRegenerateAudio = useCallback(async (clipId: string) => {
    if (!episode) return;
    setActionInProgress(`regenerate-audio-${clipId}`);
    try {
      await PodcastEpisodeService.regenerateClipAudio(episode.id, clipId);
      customToast.success('Audio regeneration started');
    } catch (err) {
      customToast.error('Failed to regenerate audio', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode]);

  const handleMergeEpisode = useCallback(async () => {
    if (!episode) return;
    primeNotificationSound();
    setActionInProgress('merge');
    try {
      await PodcastEpisodeService.mergeEpisode(episode.id);
      customToast.success('Episode merge started');
      refreshEpisode();
    } catch (err) {
      customToast.error('Failed to merge episode', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode, refreshEpisode]);

  const handleRetryMerge = useCallback(async () => {
    if (!episode) return;
    setActionInProgress('retry-merge');
    try {
      await PodcastEpisodeService.mergeEpisode(episode.id);
      customToast.success('Merge restarted');
      refreshEpisode();
    } catch (err) {
      customToast.error('Failed to retry merge', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode, refreshEpisode]);

  // ── Retry operations ───────────────────────────────────────

  const handleRetryFailedAudio = useCallback(async () => {
    if (!episode) return;
    setActionInProgress('retry-failed-audio');
    try {
      const failedClips = clips.filter(c => c.status === 'audio_failed');
      if (failedClips.length === 0) {
        customToast.info('No failed audio clips to retry');
        return;
      }
      for (const clip of failedClips) {
        await PodcastEpisodeService.regenerateClipAudio(episode.id, clip.id);
      }
      customToast.success(`Retrying audio for ${failedClips.length} clip(s)`);
      refreshEpisode();
    } catch (err) {
      customToast.error('Failed to retry audio', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode, clips, refreshEpisode]);

  const handleRetryFailedScripts = useCallback(async () => {
    if (!episode) return;
    setActionInProgress('retry-failed-scripts');
    try {
      const failedClips = clips.filter(c => c.status === 'script_failed');
      if (failedClips.length === 0) {
        customToast.info('No failed script clips to retry');
        return;
      }
      for (const clip of failedClips) {
        await PodcastEpisodeService.regenerateClipScript(episode.id, clip.id);
      }
      customToast.success(`Retrying scripts for ${failedClips.length} clip(s)`);
      refreshEpisode();
    } catch (err) {
      customToast.error('Failed to retry scripts', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode, clips, refreshEpisode]);

  const handleRetryAllFailed = useCallback(async () => {
    if (!episode) return;
    setActionInProgress('retry-all-failed');
    try {
      const scriptFailedClips = clips.filter(c => c.status === 'script_failed');
      const audioFailedClips = clips.filter(c => c.status === 'audio_failed');

      if (scriptFailedClips.length === 0 && audioFailedClips.length === 0) {
        customToast.info('No failed clips to retry');
        return;
      }

      for (const clip of scriptFailedClips) {
        await PodcastEpisodeService.regenerateClipScript(episode.id, clip.id);
      }
      for (const clip of audioFailedClips) {
        await PodcastEpisodeService.regenerateClipAudio(episode.id, clip.id);
      }

      const totalRetried = scriptFailedClips.length + audioFailedClips.length;
      customToast.success(`Retrying ${totalRetried} failed clip(s)`);
      refreshEpisode();
    } catch (err) {
      customToast.error('Failed to retry', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionInProgress(null);
    }
  }, [episode, clips, refreshEpisode]);

  // ── Publish ────────────────────────────────────────────────

  const handlePublish = useCallback(async (mode: 'publish' | 'schedule' | 'draft', scheduledDateTime?: Date) => {
    if (!episode) return;

    if (mode === 'schedule' && scheduledDateTime && scheduledDateTime <= new Date()) {
      customToast.error('Scheduled time must be in the future');
      return;
    }

    primeCelebrationSound();
    setActionInProgress('publish');
    try {
      let chapters;
      if (episode?.mergePreview && clips.length > 0) {
        chapters = PodcastEpisodeService.buildChaptersFromClips(clips, episode.mergePreview);
      }

      const effectiveStatus = mode === 'schedule' ? 'future' : mode;

      if (mode === 'schedule' && scheduledDateTime) {
        console.log('[Publish Debug] User selected:', scheduledDateTime.toString());
        console.log('[Publish Debug] ISO (UTC):', scheduledDateTime.toISOString());
        console.log('[Publish Debug] Unix timestamp:', dateToUnixTimestamp(scheduledDateTime));
        console.log('[Publish Debug] Browser timezone offset:', new Date().getTimezoneOffset(), 'minutes from UTC');
      }

      const publish_timestamp = mode === 'schedule' && scheduledDateTime
        ? dateToUnixTimestamp(scheduledDateTime)
        : undefined;

      await PodcastEpisodeService.publishToPodbean(episode.id, chapters, effectiveStatus, true, publish_timestamp);
    } catch (err) {
      customToast.error('Failed to start publishing', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
      refreshEpisode();
    } finally {
      setActionInProgress(null);
    }
  }, [episode, clips, refreshEpisode]);

  // ── Episode details ────────────────────────────────────────

  const handleSaveEpisodeDetails = useCallback(async (newTitle: string, newDescription: string) => {
    if (!episode) return;

    const updates: { title?: string; description?: string } = {};
    if (newTitle !== episode.title) updates.title = newTitle;
    if (newDescription !== episode.description) updates.description = newDescription;

    if (Object.keys(updates).length === 0) return;

    await PodcastEpisodeService.updateEpisode(episode.id, updates);

    setEpisode((prev) => prev ? {
      ...prev,
      ...(updates.title && { title: updates.title }),
      ...(updates.description !== undefined && { description: updates.description }),
    } : null);

    if (updates.title) setEditedTitle(updates.title);
    if (updates.description !== undefined) setDescription(updates.description);

    customToast.success('Episode details saved');
  }, [episode, setEpisode, setEditedTitle, setDescription]);

  const handleArticlesAdded = useCallback(() => {
    setClipsLoading(true);
    loadClips();
    refreshEpisode();
  }, [loadClips, refreshEpisode, setClipsLoading]);

  return {
    actionInProgress,
    refreshEpisode,
    handleArchive,
    handleUnarchive,
    handleDelete,
    handleDeleteClip,
    handleMoveClip,
    saveScriptForClip,
    handleGenerateScripts,
    handleApproveScripts,
    handleRegenerateScript,
    handleGenerateAudio,
    handleApproveAudio,
    handleRegenerateAudio,
    handleMergeEpisode,
    handleRetryMerge,
    handleRetryFailedAudio,
    handleRetryFailedScripts,
    handleRetryAllFailed,
    handlePublish,
    handleSaveEpisodeDetails,
    handleArticlesAdded,
  };
}
